import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Clock, CheckCircle, XCircle, Loader2, Sparkles } from 'lucide-react';
import { CatalogueSyncCard } from '@/components/admin/CatalogueSyncCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminScraping() {
  const [loading, setLoading] = useState({ togglePayment: false, toggleAuth: false });
  const [paymentModeEnabled, setPaymentModeEnabled] = useState(false);
  const [authRequired, setAuthRequired] = useState(true);
  const [autoScrapeStatus, setAutoScrapeStatus] = useState(null);
  const [enrichStatus, setEnrichStatus] = useState(null);
  const [moviesCount, setMoviesCount] = useState(0);

  useEffect(() => {
    loadPaymentSetting();
    loadAuthSetting();
    loadAutoScrapeStatus();
    loadEnrichStatus();
    loadMoviesCount();
    const interval = setInterval(() => {
      loadAutoScrapeStatus();
        loadEnrichStatus();
      loadMoviesCount();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadPaymentSetting = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/payment-mode`);
      setPaymentModeEnabled(response.data.enabled);
    } catch (_) { /* silent - non-critical status load */ }
  };

  const loadAuthSetting = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/auth-required`);
      setAuthRequired(response.data.enabled);
    } catch (_) { /* silent */ }
  };

  const loadAutoScrapeStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/auto-scrape-status`);
      setAutoScrapeStatus(response.data);
    } catch (_) { /* silent */ }
  };

  const loadEnrichStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/enrich-status`);
      setEnrichStatus(response.data);
    } catch (_) { /* silent */ }
  };

  const loadMoviesCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/movies/count`);
      setMoviesCount(response.data.count);
    } catch (_) { /* silent */ }
  };

  const togglePaymentMode = async () => {
    setLoading({ ...loading, togglePayment: true });
    try {
      const newValue = !paymentModeEnabled;
      if (newValue) {
        const resetUsers = window.confirm('Vous activez le mode PAYANT.\n\nRéinitialiser tous les utilisateurs en mode trial (15 min) ?');
        if (resetUsers) {
          await axios.post(`${API}/admin/reset-users-for-payment?admin_password=webeos123`);
        }
      }
      await axios.patch(`${API}/admin/settings/payment-mode?enabled=${newValue}`);
      setPaymentModeEnabled(newValue);
      alert(`Site maintenant en mode ${newValue ? 'PAYANT' : 'GRATUIT'}`);
    } catch (err) {
      alert('Erreur');
    } finally {
      setLoading({ ...loading, togglePayment: false });
    }
  };

  const toggleAuthRequired = async () => {
    setLoading({ ...loading, toggleAuth: true });
    try {
      const newValue = !authRequired;
      await axios.patch(`${API}/admin/settings/auth-required?enabled=${newValue}`);
      setAuthRequired(newValue);
      const status = newValue ? 'Compte REQUIS' : 'Accès LIBRE (sans compte)';
      alert(`${status}`);
    } catch (err) {
      alert('Erreur');
    } finally {
      setLoading({ ...loading, toggleAuth: false });
    }
  };

  const toggleAutoScrape = async () => {
    try {
      const newValue = !(autoScrapeStatus?.enabled);
      await axios.post(`${API}/admin/auto-scrape-toggle?enabled=${newValue}`);
      loadAutoScrapeStatus();
      alert(`Scraping automatique ${newValue ? 'activé' : 'désactivé'}`);
    } catch (err) {
      alert('Erreur');
    }
  };

  const handleEnrichMovies = async () => {
    if (enrichStatus?.running) return;
    try {
      await axios.post(`${API}/admin/enrich-movies`);
      loadEnrichStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return 'Jamais';
    try { return new Date(isoStr).toLocaleString('fr-FR'); } catch { return isoStr; }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bebas text-netflix-red">{moviesCount}</p>
            <p className="text-text-secondary font-work text-sm">Films en base</p>
          </CardContent>
        </Card>
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bebas text-white">Obiloz</p>
            <p className="text-text-secondary font-work text-sm">Source unique</p>
          </CardContent>
        </Card>
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bebas text-green-400">
              {autoScrapeStatus?.enabled ? 'Auto ON' : 'Auto OFF'}
            </p>
            <p className="text-text-secondary font-work text-sm">Statut</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Mode */}
      <Card className="bg-bg-secondary border-white/10 border-2 border-netflix-red/40">
        <CardHeader>
          <CardTitle className="text-white font-work">Mode de Paiement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
            <div className="flex-1">
              <h3 className="text-white font-work font-semibold text-xl">
                {paymentModeEnabled ? 'Mode PAYANT' : 'Mode GRATUIT'}
              </h3>
              <p className="text-text-secondary text-sm">
                {paymentModeEnabled ? 'Les utilisateurs doivent payer' : 'Accès gratuit pour tous'}
              </p>
            </div>
            <Button
              onClick={togglePaymentMode}
              disabled={loading.togglePayment}
              className={paymentModeEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {loading.togglePayment ? <RefreshCw className="w-4 h-4 animate-spin" /> : paymentModeEnabled ? 'Rendre Gratuit' : 'Rendre Payant'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auth Required Toggle */}
      <Card className="bg-bg-secondary border-white/10 border-2 border-blue-500/40">
        <CardHeader>
          <CardTitle className="text-white font-work">Accès au Site</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
            <div className="flex-1">
              <h3 className="text-white font-work font-semibold text-xl">
                {authRequired ? 'Compte REQUIS' : 'Accès LIBRE'}
              </h3>
              <p className="text-text-secondary text-sm">
                {authRequired 
                  ? 'Les visiteurs doivent créer un compte pour regarder les films'
                  : 'N\'importe qui avec le lien peut regarder les films sans compte'}
              </p>
            </div>
            <Button
              onClick={toggleAuthRequired}
              disabled={loading.toggleAuth}
              className={authRequired ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              {loading.toggleAuth ? <RefreshCw className="w-4 h-4 animate-spin" /> : authRequired ? 'Rendre Libre' : 'Exiger un Compte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <CatalogueSyncCard />

      {/* Enrichissement TMDB */}
      <Card className="bg-bg-secondary border-white/10 border-2 border-purple-500/40">
        <CardHeader>
          <CardTitle className="text-white font-work flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Enrichissement TMDB
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-text-secondary text-sm font-inter">
            Nettoie les titres et récupère les affiches, synopsis, notes, genres et acteurs depuis TMDB pour tous les films qui en manquent.
          </p>

          {enrichStatus?.running ? (
            <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                <span className="text-purple-200 font-work font-semibold">Enrichissement en cours...</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-purple-400 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${enrichStatus.total > 0 ? Math.round(enrichStatus.processed / enrichStatus.total * 100) : 0}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="text-center p-2 bg-white/5 rounded">
                  <p className="text-2xl font-bebas text-white">{enrichStatus.processed}/{enrichStatus.total}</p>
                  <p className="text-text-secondary text-xs">Traités</p>
                </div>
                <div className="text-center p-2 bg-white/5 rounded">
                  <p className="text-2xl font-bebas text-green-400">{enrichStatus.enriched}</p>
                  <p className="text-text-secondary text-xs">Enrichis</p>
                </div>
                <div className="text-center p-2 bg-white/5 rounded">
                  <p className="text-2xl font-bebas text-red-400">{enrichStatus.failed}</p>
                  <p className="text-text-secondary text-xs">Non trouvés</p>
                </div>
              </div>
              <p className="text-purple-200/80 text-xs font-inter">{enrichStatus.progress}</p>
            </div>
          ) : (
            <>
              <Button
                onClick={handleEnrichMovies}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-work font-bold py-6 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Enrichir tous les films sans affiche
              </Button>
              {enrichStatus?.finished_at && (
                <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg text-sm">
                  <p className="text-green-200 font-work font-semibold mb-1">Dernier enrichissement :</p>
                  <p className="text-text-secondary text-xs">
                    {enrichStatus.enriched} enrichis, {enrichStatus.failed} non trouvés, {enrichStatus.cleaned} titres nettoyés
                  </p>
                  <p className="text-text-secondary text-xs">Terminé : {formatDate(enrichStatus.finished_at)}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Auto Scraping */}
      <Card className="bg-bg-secondary border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-work flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-400" />
            Scraping Automatique
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {autoScrapeStatus?.enabled ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                <h3 className="text-white font-work font-semibold">{autoScrapeStatus?.enabled ? 'Activé' : 'Désactivé'}</h3>
              </div>
              <div className="space-y-1 text-sm text-text-secondary font-inter">
                <p>Intervalle : toutes les {autoScrapeStatus?.interval_hours || 12}h</p>
                <p>Dernière exécution : {formatDate(autoScrapeStatus?.last_run)}</p>
              </div>
            </div>
            <Button onClick={toggleAutoScrape} className={autoScrapeStatus?.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}>
              {autoScrapeStatus?.enabled ? 'Désactiver' : 'Activer'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
